import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, } from '@ngrx/store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, retryWhen, switchMap, take, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { LocalStorageService } from '../../../core/local-storage/local-storage.service';
import { Persona } from '../../models';

import {
    InitPersona,
    InitPersonaSuccess,
    Load,
    LoadSuccess,
    PersonaActionFail,
    PersonaActionTypes,
    SetPersona
} from './persona.actions';
import { HTTP_STATUS_404 } from '../../../constants/application-constants';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { personaSelectionViewedKey } from '@app/persona/store/personas/persona.reducers';
import { SET_LOGGED_IN } from '@app/store/auth/auth.actions';
import { PersonaTypes } from '@app/persona/constants/constants';

@Injectable()
export class PersonaEffects implements OnInitEffects {
    personaUrl = `${environment.csBaseURL}/organisationgroup?type=persona`;

    persistPersonaSelectionViewed$ = createEffect(() => this.actions$.pipe(
        ofType(PersonaActionTypes.SET_SKIP_SELECTION, PersonaActionTypes.SET_PERSONA),
        tap(_ => this.localStorageService.setItem(personaSelectionViewedKey, true))
    ), { dispatch: false }
    );

    // reload when logging out SetLoggedIn

    reloadOnLoginLogout$ = createEffect(() => this.actions$.pipe(
        ofType(SET_LOGGED_IN),
        map(() => new InitPersona())
    ));


    init$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(PersonaActionTypes.INIT_PERSONA),
        switchMap((persona: Persona) => getPersona(this.personaUrl, this.httpClient, this.notification)),
        filter(action => action instanceof LoadSuccess),
        mergeMap((action: LoadSuccess) => {
            const items = action.items;
            const catalogueFilter = items.find(item => item.type === PersonaTypes.FILTER);
            const initialPersona = this.getInitialPersona(items);
            const persona = !!catalogueFilter ? catalogueFilter : initialPersona;
            return [new LoadSuccess(items), new InitPersonaSuccess(persona)];
        })));


    set$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(PersonaActionTypes.SET_PERSONA),
        tap((action: SetPersona) => {
            const { persona } = action;
            // dont save catalogue_filters to storage
            if (!persona || (persona && persona.type !== PersonaTypes.FILTER)) {
                this.localStorageService.setItem('Persona', persona);
            }
        })), { dispatch: false });


    load$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(PersonaActionTypes.LOAD),
        switchMap((action: Load) => getPersona(this.personaUrl, this.httpClient, this.notification))
    ));

    constructor(private actions$: Actions,
        private httpClient: HttpClient,
        private localStorageService: LocalStorageService,
        private notification: NzNotificationService) {
    }

    ngrxOnInitEffects(): Action {
        return new InitPersona();
    }

    private getInitialPersona(items: Persona[]): Persona {
        const personaFromStorage: Persona = this.localStorageService.getItem('Persona');
        const testPersona = item => item.organisationGroupId === personaFromStorage.organisationGroupId;
        const isInPersonaList = (list: Persona[]) => list.find(testPersona);
        const isValidPersonaFromStorage = !!personaFromStorage && isInPersonaList;
        return isValidPersonaFromStorage ? personaFromStorage : null;
    }

}

const getSanitizedMaskName = ({ contextOrganisationName, name }: Persona) => !!contextOrganisationName
    ? contextOrganisationName
    : name;
const getMaskedNamePersona = (item: Persona) => ({ ...item, name: getSanitizedMaskName(item) });
const maskGroupNameFn = (item: Persona) => item.type === PersonaTypes.FILTER ? getMaskedNamePersona(item) : item;
const maskGroupNameWithOrgNameOnCatalogueFilterFn = (items: Persona[]) => items.map(maskGroupNameFn);
const getPersonaOrganisationGroupIds = (item: Persona) =>
    item.catalogueFilterOrganisationGroupIds ? item.catalogueFilterOrganisationGroupIds : [item.organisationGroupId];
const mapPersonaOrganisationGroupIds = (items: Persona[]) =>
    items.map(item => ({ ...item, personaOrganisationGroupIds: getPersonaOrganisationGroupIds(item) }));
const getPersona =
    (personaUrl: string, http: HttpClient, notification: NzNotificationService): Observable<LoadSuccess | PersonaActionFail> =>
        http.get<Persona[]>(personaUrl, {}).pipe(
        // This call might fail when there are online_identity mapping issues, which will be fixed by an auth effect
        // we can allow this to fail and the SetLoggedIn will automatically reload
            retryWhen(errors => errors.pipe(delay(1000), take(2))),
            map(maskGroupNameWithOrgNameOnCatalogueFilterFn),
            map(mapPersonaOrganisationGroupIds),
            map(items => new LoadSuccess(items)),
            catchError(err => {
                const msg = err.status === HTTP_STATUS_404 ? err.error : 'Sorry, something went wrong.';
                notification.error('Failed to load Personas', msg);
                return [new PersonaActionFail()];
            }
            ));

